import React from "react";
import { Avatar, Card, CardHeader } from "@opendate-app/setlist";

import { useBroadcastEditContext } from "../BroadcastEditContainer";
import { audienceSummary } from "../Composer/InboxPreview";
import { senderLabel } from "../Composer/smsMessage";
import { formatCount } from "./reportMath";

const styles = {
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))",
    gap: 16,
  },
  list: {
    margin: 0,
  },
  row: {
    display: "grid",
    gridTemplateColumns: "40% 1fr",
    gap: 16,
    padding: "14px 24px",
    borderTop: "1px solid var(--sl-border-secondary)",
    fontSize: 14,
  },
  term: {
    color: "var(--sl-text-tertiary)",
    fontWeight: 400,
  },
  value: {
    margin: 0,
    color: "var(--sl-text-primary)",
    overflowWrap: "anywhere",
  },
};

const DetailCard = ({ number, title, rows }) => (
  <Card padding="none" header={<CardHeader leading={<Avatar size="sm" initials={String(number)} alt="" />} title={title} />}>
    <dl style={styles.list}>
      {rows.map(([term, value]) => (
        <div key={term} style={styles.row}>
          <dt style={styles.term}>{term}</dt>
          <dd style={styles.value}>{value || "—"}</dd>
        </div>
      ))}
    </dl>
  </Card>
);

const ReportDetails = ({ authorship }) => {
  const {
    broadcast,
    overviewMetrics,
    emailSendDomain,
    sendDomain,
    sentAt,
    formatDate,
    timeZone,
    isEmailTemplate,
    twilioMessagingServices,
  } = useBroadcastEditContext();

  const sender = emailSendDomain?.name && sendDomain
    ? `${emailSendDomain.name} <${sendDomain.full_from_address}>`
    : null;
  const editedBy = authorship.last_edited_by ? ` by ${authorship.last_edited_by}` : "";

  if (!isEmailTemplate) {
    const smsSender = twilioMessagingServices?.find((service) => service.id === broadcast.twilio_template.twilio_messaging_service_id);

    return (
      <div style={styles.grid}>
        <DetailCard
          number={1}
          title="Recipients"
          rows={[
            ["Audience", audienceSummary(broadcast, true)],
            ["Sent to", `${formatCount(overviewMetrics.sent_count)} fans`],
          ]}
        />
        <DetailCard
          number={2}
          title="Message"
          rows={[
            ["From", smsSender && senderLabel(smsSender)],
            ["Created by", authorship.created_by],
            ["Last edited", `${formatDate(authorship.last_edited_at)}${editedBy}`],
          ]}
        />
        <DetailCard
          number={3}
          title="Schedule"
          rows={[
            ["Sent", formatDate(sentAt)],
            ["Time zone", timeZone],
          ]}
        />
      </div>
    );
  }

  return (
    <div style={styles.grid}>
      <DetailCard
        number={1}
        title="Recipients"
        rows={[
          ["Audience", audienceSummary({ ...broadcast, target_engaged_audience: false }, true)],
          ["Engaged only", broadcast.target_engaged_audience ? "On" : "Off"],
          ["Sent to", `${formatCount(overviewMetrics.sent_count)} fans`],
        ]}
      />
      <DetailCard
        number={2}
        title="Sender"
        rows={[
          ["From", sender],
          ["Subject", broadcast.subject],
          ["Preview text", broadcast.preheader],
        ]}
      />
      <DetailCard
        number={3}
        title="Content"
        rows={[
          ["Template", broadcast.beefree_template?.copied_from_blueprint?.name || "Custom design"],
          ["Created by", authorship.created_by],
          ["Last edited", `${formatDate(authorship.last_edited_at)}${editedBy}`],
        ]}
      />
      <DetailCard
        number={4}
        title="Schedule"
        rows={[
          ["Sent", formatDate(sentAt)],
          ["Time zone", timeZone],
          ["Transactional", broadcast.transactional ? "Yes" : "No"],
        ]}
      />
    </div>
  );
};

export default ReportDetails;
